import React, { Suspense, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  styled,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { Product, SetFlag } from "../../../utils";
import { useNavigate } from "react-router-dom";
import { useUtility } from "utils/context/UtilityContext";

const BSBCoin = React.lazy(() => import("../BSBCoin"));

const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[6],
  },
  cursor: "pointer",
  position: "relative",
}));

const BestsellerTag = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1.5),
  right: theme.spacing(1.5),
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.shape.borderRadius,
  fontSize: "0.8rem",
  fontWeight: "bold",
  zIndex: 1,
}));

// --- Main Component ---

interface ProductCardProps {
  product?: Product; // Product is optional when loading
  loading?: boolean; // Add loading prop
}

// A Skeleton component for the loading state
const ProductCardSkeleton = () => (
  <StyledCard>
    <Skeleton variant="rectangular" height={180} />
    <CardContent>
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 2,
          gap: 2,
        }}
      >
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="circular" width={40} height={40} />
      </Box>
    </CardContent>
  </StyledCard>
);

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  loading = false,
}) => {
  const { addToCart } = useUtility();
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // If loading is true, render the skeleton.
  if (loading || !product) {
    return <ProductCardSkeleton />;
  }

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart(product);
    setSnackbarOpen(true);
  };

  const handleNavigate = () => {
    navigate(`/provider/${product.provider}`);
  };

  return (
    <>
      <StyledCard onClick={handleNavigate}>
        {product.isBestseller && <BestsellerTag>Bestseller</BestsellerTag>}
        <CardMedia
          component="img"
          image={product.image}
          alt={product.name}
          sx={{ height: 180, objectFit: "contain", p: 2 }}
        />
        <CardContent
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
          }}
        >
          <Box flexGrow={1}>
            <Box display="flex" justifyContent="center" mb={1} gap={1}>
              <Typography
                gutterBottom
                variant="h6"
                component="div"
                sx={{ fontWeight: "600", minHeight: "64px" }}
              >
                {product.name}
              </Typography>
              <SetFlag
                countryName={product.country || ""}
                countryCode={product.countryCode}
              />
            </Box>

            <Typography
              variant="h6"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1.25,
                py: 0.5,
                borderRadius: 2,
                fontWeight: 800,
                backgroundColor: "rgba(37,76,120,0.10)", // léger fond
                color: "text.primary",
              }}
            >
              {product.price} {product.currency}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 2,
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Suspense fallback={<CircularProgress size={20} />}>
                <BSBCoin />
              </Suspense>
              <Typography
                variant="h5"
                color="primary"
                sx={{ fontWeight: "bold" }}
              >
                {product.priceUSD}
              </Typography>
            </Box>
            <Tooltip title="Add to Cart">
              <IconButton
                onClick={handleAddToCart}
                color="primary"
                sx={{ border: "1px solid", borderColor: "primary.main" }}
              >
                <AddShoppingCartIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </StyledCard>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Added to cart!
        </Alert>
      </Snackbar>
    </>
  );
};
